<template>
	<ValidationForm #default="{ handleSubmit }">
		<SSOConfigurationLayout
			:title="pageTitle"
			:caption="pageCaption"
		>
			<template #content>
				<StepperAccordion
					:steps="transformedSteps"
					:initial-step="step + 1"
					:current-step-index="step"
					:show-actions="false"
					@step-change="handleStepChange"
				>
					<template #step-0>
						<validation-observer
							v-if="step === 0"
							ref="stepObs0"
						>
							<QuestionGroup>
								<SSOSetupDetailsCard
									:sso-configuration.sync="ssoConfiguration"
									:user-full-permissions="userFullPermissions"
									:lookup-data="lookupData"
									:is-configuration-complete="isConfigurationComplete"
								/>
								<transition name="fade-expand">
									<SSOProviderSettingsCard
										v-if="ssoConfiguration.tenantGuid"
										key="provider-settings"
										:sso-configuration="ssoConfiguration"
										:lookup-data="lookupData"
									/>
								</transition>
							</QuestionGroup>
						</validation-observer>
					</template>
					<template #step-1>
						<validation-observer
							v-if="step === 1"
							ref="stepObs1"
						>
							<SSOConfigurationCard
								v-if="ssoConfiguration.ssoProviderId"
								:sso-configuration.sync="ssoConfiguration"
								:user-full-permissions="userFullPermissions"
								:lookup-data="lookupData"
								:is-edit="isEdit"
								:force-connection-details="!ssoConfigurationToManage.clientID"
							/>
						</validation-observer>
					</template>
					<template #step-2>
						<validation-observer
							v-if="step === 2"
							ref="stepObs2"
						>
							<SSOMappingCard
								v-if="ssoConfiguration.ssoProviderId"
								:sso-configuration.sync="ssoConfiguration"
								:user-full-permissions="userFullPermissions"
								:lookup-data="lookupData"
								:product-roles="productRoles"
								@show-mapping-modal="updateShowMappingModal"
								@delete-claim="setClaimToRemove"
							/>
						</validation-observer>
					</template>
				</StepperAccordion>
			</template>
			<template #footer>
				<PageActionRow>
					<template #actions>
						<v-row
							dense
							class="d-flex flex-grow-1 justify-center"
						>
							<v-col
								cols="12"
								md="12"
								lg="12"
								xl="8"
								class="flex-grow-1"
							>
								<div
									class="d-flex justify-space-between align-center flex-grow-1"
									style="width: 100%"
								>
									<!-- Left: Back to Overview -->
									<SecondaryActionButton
										test-id="manage-sso-configuration-back-to-overview"
										@click="backToOverview"
									>
										Back to Overview
									</SecondaryActionButton>

									<!-- Middle: Step indicator -->
									<span class="stepper-footer__step-indicator">
										Step {{ step + 1 }} of {{ steps.length }}
									</span>

									<!-- Right: Actions; ensure Next is last -->
									<div class="d-flex cassie-horizontal-sm">
										<template v-if="userFullPermissions">
											<SecondaryActionButton
												v-if="step > 0"
												class="mr-2"
												@click="step = step - 1"
											>
												Previous
											</SecondaryActionButton>

											<PrimaryActionButton
												v-if="!isLastStep && (step === 0 || isEdit)"
												class="mr-2"
												test-id="manage-sso-configuration-save"
												@click="handleSubmit(() => submit({ stepOneOnly: setStepOneOnly(), stayOnTab: true }))"
											>
												Save Changes
											</PrimaryActionButton>

											<PrimaryActionButton
												:test-id="isLastStep ? 'manage-sso-configuration-submit' : 'manage-sso-configuration-next'"
												:disabled="step === 0 && !ssoConfiguration.ssoProviderId"
												@click="isLastStep ? handleSubmit(submit) : validateCurrentStepAndNext()"
											>
												{{ isLastStep ? (isEdit ? 'Save Changes' : 'Create') : 'Next' }}
											</PrimaryActionButton>
										</template>
									</div>
								</div>
							</v-col>
						</v-row>
					</template>
				</PageActionRow>
			</template>
		</SSOConfigurationLayout>
		<SSOManageMapping
			v-if="showMappingModal"
			:is-open="showMappingModal"
			:lookup-data="lookupData"
			:sso-configuration="ssoConfiguration"
			:user-full-permissions="userFullPermissions"
			:sso-mapping-to-manage="ssoMappingToManage"
			:product-roles="productRoles"
			@close-modal="closeMappingModal"
			@update-sso-mapping="updateSsoMapping"
		/>
		<ConfirmDeleteModal
			v-if="claimToRemove"
			:entity-name="claimToRemove.cassieModuleName"
			entity-type="Claim"
			@close="claimToRemove = null"
			@delete="deleteClaim"
		/>
	</ValidationForm>
</template>

<script>
import { mapGetters } from 'vuex'
import { ValidationObserver } from 'vee-validate'
import ValidationForm from '../../../../../../shared/components/validation-form.vue'
import SSOConfigurationLayout from './sso-configuration-layout.vue'
import SSOSetupDetailsCard from './sso-setup-details-card.vue'
import SSOConfigurationCard from './sso-configuration-card.vue'
import SSOMappingCard from './sso-mapping-card.vue'
import SSOManageMapping from './manage-sso-mapping.vue'
import ConfirmDeleteModal from '../../../../../../shared/components/confirm-delete-modal.vue'
import PageActionRow from '../../../../../../shared/components/page-action-row.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import { SSO_CONFIGURATION } from '../../../../router/route-names.js'
import { SSO_CONFIGURATION_FULL_PERMISSIONS } from '../../../../../../shared/permissions/admin-portal-permissions.js'
import { getProductRoles } from '../../../../../../shared/utils/api/product-roles.js'
import { addAdminPortalUserIfNotExist, addCSPortalUserIfNotExist, ssoCspUserIdentityField, reloadConfigKeys } from '../../../../../../shared/state/config-keys.js'
import { postSSOConfiguration, putSSOConfiguration, toggleSSOConfiguration, createScimToken } from '../../../../../../shared/utils/api/sso-configuration.js'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import SSOProviderSettingsCard from './sso-provider-settings-card.vue'
import { StepperAccordion, QuestionGroup } from '../../../../components/component-library-wrapper.js'
export default {
	components: {
		ValidationObserver,
		ValidationForm,
		SSOConfigurationLayout,
		SSOSetupDetailsCard,
		SSOConfigurationCard,
		SSOMappingCard,
		SSOManageMapping,
		ConfirmDeleteModal,
		PageActionRow,
		PrimaryActionButton,
		SecondaryActionButton,
		SSOProviderSettingsCard,
		StepperAccordion,
		QuestionGroup
	},
	props: {
		ssoConfigurationToManage: Object,
		lookupData: Object,
		addAdminPortalUserIfNotExist: Boolean,
		addCSPortalUserIfNotExist: Boolean,
		ssoCspUserIdentityField: String
	},
	setup () {
		return {
			SSO_CONFIGURATION,
			reloadConfigKeys
		}
	},
	data () {
		return {
			ssoConfiguration: {},
			showMappingModal: false,
			ssoMappingToManage: null,
			productRoles: [],
			claimToRemove: null,
			step: 0,
			localSsoConfigurationToManage: {},
			OTHER_PROVIDER: 5
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		selectedProviderName () {
			const provider = this.lookupData?.ssoProviders?.find(p => p.id === this.ssoConfiguration?.ssoProviderId)
			return provider?.ssoProviderName || ''
		},
		pageTitle () {
			return `SSO Configuration - ${this.ssoConfiguration.productName}`
		},
		pageCaption () {
			return `Edit and manage SSO Configuration for the Syrenis Administration Portal or Customer Service Portal`
		},
		isEdit () {
			return this.localSsoConfigurationToManage.createdDate !== 'Not Configured'
		},
		notConfigured () {
			return this.ssoConfigurationToManage?.id === 0
		},
		isInProgress () {
			return Boolean(this.ssoConfigurationToManage?.id) &&
			!this.ssoConfigurationToManage.authority && !this.ssoConfigurationToManage.remoteLogoutEndpointUrl && !this.ssoConfigurationToManage.identityClaimTypeName
		},
		userFullPermissions () {
			return this.productAreaPermission(SSO_CONFIGURATION_FULL_PERMISSIONS)
		},
		steps () {
			return [
				{ title: 'Details', slot: 0, stepNumber: 0 },
				{ title: 'Configuration', slot: 1, stepNumber: 1 },
				{ title: 'Mapping', slot: 2, stepNumber: 2 }
			]
		},
		transformedSteps () {
			// Mirror the behavior used in manage-integration-service-layout
			return this.steps.map((s, index) => ({
				title: s.title,
				subtitle: index === 0
					? 'Provide system details and tenant settings'
					: index === 1
						? 'Configure provider settings'
						: 'Setup claims within Syrenis to set User Roles & Brands upon login from an SSO Provider',
				disabled: !this.isEdit && index > this.step && (!this.ssoConfiguration || !this.ssoConfiguration.ssoProviderId),
				stepComplete: index < this.step
			}))
		},
		isLastStep () {
			return this.step === this.steps.length - 1
		},
		isConfigurationComplete () {
			// A configuration is "complete" when it has all required fields to be enabled
			// This mirrors the canBeEnabled logic from the overview page
			const has = v => typeof v === 'string' ? v.trim().length > 0 : v != null && v !== false
			const config = this.ssoConfigurationToManage
			return config.id > 0 &&
				has(config.authority) &&
				has(config.remoteLogoutEndpointUrl) &&
				has(config.identityClaimTypeName) &&
				has(config.clientID)
		}
	},
	async created () {
		this.ssoConfiguration = {
			...this.ssoConfigurationToManage,
			isEnabled: this.isEdit ? this.ssoConfigurationToManage.isEnabled : false,
			editConnectionDetails: !this.ssoConfigurationToManage.clientID,
			clientSecret: null,
			mappedClaims: this.mapClaims(),
			vueAdminPortalURL: this.lookupData.vueAdminPortalURL,
			vueCSPortalURL: this.lookupData.vueCSPortalURL,
			originalClientId: this.ssoConfigurationToManage.clientID,
			originalAuthority: this.ssoConfigurationToManage.authority,
			originalIdentityClaimTypeName: this.ssoConfigurationToManage.identityClaimTypeName,
			originalRemoteLogoutEndpointUrl: this.ssoConfigurationToManage.remoteLogoutEndpointUrl
		}
		this.ssoConfiguration.addAdminPortalUserIfNotExist = addAdminPortalUserIfNotExist.value
		this.ssoConfiguration.addCSPortalUserIfNotExist = addCSPortalUserIfNotExist.value
		if (this.ssoConfiguration.productId === 2) {
			this.ssoConfiguration.ssoCspUserIdentityField =
      this.ssoConfiguration.ssoCspUserIdentityField ||
      ssoCspUserIdentityField.value ||
      'Username'
		}
		delete this.ssoConfiguration.clientID
		await this.getProductRoles()
		this.localSsoConfigurationToManage = {
			...this.ssoConfigurationToManage
		}
	},
	methods: {
		async validateCurrentStepAndNext () {
			const refName = `stepObs${this.step}`
			const observer = this.$refs[refName]
			if (observer && typeof observer.validate === 'function') {
				const valid = await observer.validate()
				if (valid) this.step = this.step + 1
			} else {
				// Fallback: if no observer for step, just advance
				this.step = this.step + 1
			}
		},
		handleStepChange (stepIndex) {
			// StepperAccordion emits 1-based index
			const targetStep = stepIndex - 1
			if (this.isEdit) {
				this.step = targetStep
				return
			}
			// In create mode, allow navigation to completed or current/next sequential step
			if (targetStep <= this.step || this.transformedSteps[targetStep]?.stepComplete) {
				this.step = targetStep
			}
		},
		async getProductRoles () {
			const { data: productRoles } = await getProductRoles(this.ssoConfigurationToManage.productId)
			this.productRoles = productRoles
		},
		updateShowMappingModal (mapping) {
			if (mapping) {
				this.ssoMappingToManage = mapping
			}
			this.showMappingModal = true
		},
		closeMappingModal () {
			this.showMappingModal = false
			this.ssoMappingToManage = null
		},
		updateSsoMapping (mapping) {
			const componentTypeName = this.lookupData.ssoComponentTypes.find(ct => ct.id === mapping[0].componentTypeId).componentTypeName
			const matchingClaim = this.ssoConfiguration.mappedClaims.find(claim => claim.cassieModuleName === componentTypeName)
			if (matchingClaim) {
				matchingClaim.claims = mapping
			} else {
				this.ssoConfiguration.mappedClaims.push({
					cassieModuleName: componentTypeName,
					claims: mapping
				})
			}
			this.closeMappingModal()
		},
		setClaimToRemove (claim) {
			this.claimToRemove = claim
		},
		deleteClaim () {
			this.$delete(this.ssoConfiguration.mappedClaims, this.ssoConfiguration.mappedClaims.indexOf(this.claimToRemove))
			this.claimToRemove = null
		},
		backToOverview () {
			this.$router.push({ name: SSO_CONFIGURATION })
		},
		computeConfigurationName () {
			const { ssoProviderId, name } = this.ssoConfiguration || {}
			// If "Other" provider is selected and a custom name is provided, append product name
			if (ssoProviderId === this.OTHER_PROVIDER && name) {
				return name
			}
			// Otherwise fallback to "<Provider> - <Product>"
			return this.selectedProviderName
		},
		async submit ({ stepOneOnly = false, stayOnTab = false } = {}) {
			if (this.ssoConfiguration.clientId && !this.ssoConfiguration.clientSecret) {
				showSnackbar({ text: 'Client Secret is required when Client ID is provided.', color: 'red' })
				return
			}
			if (this.ssoConfiguration.clientSecret && !this.ssoConfiguration.clientId) {
				showSnackbar({ text: 'Client ID is required when Client Secret is provided.', color: 'red' })
				return
			}

			// Extract pending SCIM token before building payload
			const pendingScimToken = this.ssoConfiguration.pendingScimToken

			// todo - name
			const payload = {
				...this.ssoConfiguration,
				name: this.computeConfigurationName(),
				description: this.computeConfigurationName(),
				isEnabled: this.ssoConfiguration.isEnabled,
				claims: stepOneOnly ? [] : this.ssoConfiguration.mappedClaims.flatMap(claim => claim.claims),
				clientId: stepOneOnly || !this.ssoConfiguration.editConnectionDetails ? null : this.ssoConfiguration.clientId,
				clientSecret: stepOneOnly || !this.ssoConfiguration.editConnectionDetails ? null : this.ssoConfiguration.clientSecret,
				addUserIfNotExist: this.ssoConfiguration.productId === 1
					? this.ssoConfiguration.addAdminPortalUserIfNotExist
					: this.ssoConfiguration.addCSPortalUserIfNotExist
			}
			delete payload.mappedClaims
			delete payload.editConnectionDetails
			delete payload.createdDate
			delete payload.dateCreated
			delete payload.productName
			delete payload.addAdminPortalUserIfNotExist
			delete payload.addCSPortalUserIfNotExist
			delete payload.pendingScimToken
			delete payload.scimToken
			let configId = this.localSsoConfigurationToManage.id
			if (this.isEdit) {
				await putSSOConfiguration(payload, configId)
			} else {
				const response = await postSSOConfiguration(payload)
				configId = response.data.createdSsoConfigurationId
				this.localSsoConfigurationToManage = {
					...this.localSsoConfigurationToManage,
					id: configId,
					createdDate: new Date().toISOString()
				}
			}
			await toggleSSOConfiguration({
				enabled: this.ssoConfiguration.isEnabled,
				productId: this.ssoConfiguration.productId,
				systemConfigurationId: configId
			})

			// Create SCIM token if one is pending (now that we have the configId)
			if (pendingScimToken && this.ssoConfiguration.scimEnabled) {
				try {
					await createScimToken(configId, pendingScimToken)
					// Clear the pending token from local state
					this.ssoConfiguration.pendingScimToken = null
				} catch (error) {
					console.error('Failed to create SCIM token', error)
					showSnackbar({ text: 'SSO Configuration saved but SCIM token creation failed.', color: 'warning' })
				}
			}

			if (stepOneOnly) {
				showSnackbar('Setup Details saved successfully')
				if (!stayOnTab) {
					this.step = 1
				}
				return
			}
			try {
				await reloadConfigKeys()
			} catch {
				showSnackbar('Could not reload configuration keys.', { color: 'error' })
			}
			showSnackbar(`SSO Configuration ${this.isEdit ? 'saved' : 'created'} successfully`)
			this.backToOverview()
		},
		mapClaims () {
			const claims = []
			this.lookupData.ssoComponentTypes.forEach(componentType => {
				const matchedComponentType = this.ssoConfigurationToManage.claims?.filter(claim => claim.componentTypeId === componentType.id)
				if (matchedComponentType?.length > 0) {
					claims.push({
						cassieModuleName: componentType.componentTypeName,
						claims: matchedComponentType ?? []
					})
				}
			})
			return claims
		},
		setStepOneOnly () {
			return this.notConfigured && this.step === 0
		}
	}
}
</script>

<style scoped>
/* Subtle fade & upward slide */
.fade-expand-enter-active,
.fade-expand-leave-active { transition: opacity .24s ease, transform .24s ease; }
.fade-expand-enter,
.fade-expand-leave-to { opacity:0; transform:translateY(-6px); }
.fade-expand-enter-to,
.fade-expand-leave { opacity:1; transform:translateY(0); }
</style>
